import React, { useMemo, useState } from 'react';
import {
    Box,
    Card,
    Typography,
    useTheme,
    Skeleton,
    alpha,
    CardContent,
    Chip,
    Button,
    Divider,
    Stack,
    Collapse,
    IconButton,
    Grid,
} from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    ErrorOutline as ErrorOutlineIcon,
    WarningAmber as WarningAmberIcon,
    Assessment as AssessmentIcon,
    VisibilityOff as VisibilityOffIcon,
    Check as CheckIcon,
    ExpandMore as ExpandMoreIcon,
    CalendarToday as CalendarTodayIcon,
    AccountBalanceWallet as WalletIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from '../../utils/formatters';
import PageLayout from '../common/PageLayout';

interface FilterCard {
    key: string;
    label: string;
    count: number;
    impact?: string;
    color: string;
    icon: React.ReactNode;
    description: string;
}

interface SmartCheckOverviewProps {
    costBasisIssues: Array<{ gain: number; currency: string; taxYear?: number }>;
    missingPriceIssues: Array<{ currency: string; taxYear?: number }>;
    ignoredIssuesCount: number;
    ignoredIssuesData: Array<{ currency: string; taxYear?: number }>;
    highIgnoredCount: number;
    lowIgnoredCount: number;
    pricingIgnoredCount: number;
    userCurrency: string;
    isLoading: boolean;
}

interface FilterSelections {
    [key: string]: {
        years: Set<number>;
        assets: Set<string>;
    };
}

// Helper function to parse text with ** markers for emphasis
const parseEmphasisText = (text: string): { text: string; emphasized: boolean }[] => {
    const parts: { text: string; emphasized: boolean }[] = [];
    const regex = /\*\*(.*?)\*\*/g;
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(text)) !== null) {
        // Add text before the match
        if (match.index > lastIndex) {
            parts.push({ text: text.substring(lastIndex, match.index), emphasized: false });
        }
        // Add the emphasized text
        parts.push({ text: match[1], emphasized: true });
        lastIndex = regex.lastIndex;
    }

    // Add remaining text
    if (lastIndex < text.length) {
        parts.push({ text: text.substring(lastIndex), emphasized: false });
    }

    return parts;
};

const SmartCheckOverview: React.FC<SmartCheckOverviewProps> = ({
    costBasisIssues,
    missingPriceIssues,
    ignoredIssuesCount,
    ignoredIssuesData,
    highIgnoredCount,
    lowIgnoredCount,
    pricingIgnoredCount,
    userCurrency,
    isLoading,
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();

    // Track selections per filter card
    const [selections, setSelections] = useState<FilterSelections>({});
    const [expandedAssets, setExpandedAssets] = useState<Set<string>>(new Set());
    const [expandedFilters, setExpandedFilters] = useState<Set<string>>(new Set());

    // Calculate high/low impact issues
    const highImpactIssues = costBasisIssues.filter(issue => Math.abs(issue.gain) > 10);
    const lowImpactIssues = costBasisIssues.filter(issue => Math.abs(issue.gain) <= 10);

    const highImpactAmount = highImpactIssues.reduce((sum, issue) => sum + Math.abs(issue.gain), 0);
    const lowImpactAmount = lowImpactIssues.reduce((sum, issue) => sum + Math.abs(issue.gain), 0);

    const totalIssues = costBasisIssues.length + missingPriceIssues.length;

    // Get years and assets per filter with dynamic cross-filtering
    const getFilterData = (filterKey: string, currentSelections?: { years: Set<number>; assets: Set<string> }) => {
        let issues: Array<{ currency: string; taxYear?: number }> = [];

        switch (filterKey) {
            case 'all':
                issues = [...costBasisIssues, ...missingPriceIssues];
                break;
            case 'high':
                issues = highImpactIssues;
                break;
            case 'low':
                issues = lowImpactIssues;
                break;
            case 'pricing':
                issues = missingPriceIssues;
                break;
            case 'ignored':
                issues = ignoredIssuesData;
                break;
        }

        // Apply cross-filtering based on current selections
        let filteredIssues = issues;

        if (currentSelections) {
            // Filter by selected years
            if (currentSelections.years.size > 0) {
                filteredIssues = filteredIssues.filter(issue =>
                    issue.taxYear && currentSelections.years.has(issue.taxYear)
                );
            }

            // Filter by selected assets
            if (currentSelections.assets.size > 0) {
                filteredIssues = filteredIssues.filter(issue =>
                    issue.currency && currentSelections.assets.has(issue.currency)
                );
            }
        }

        // Count by year from filtered issues
        const yearCounts = new Map<number, number>();
        filteredIssues.forEach(issue => {
            if (issue.taxYear) {
                yearCounts.set(issue.taxYear, (yearCounts.get(issue.taxYear) || 0) + 1);
            }
        });

        // Count by asset from filtered issues
        const assetCounts = new Map<string, number>();
        filteredIssues.forEach(issue => {
            if (issue.currency) {
                assetCounts.set(issue.currency, (assetCounts.get(issue.currency) || 0) + 1);
            }
        });

        const years = Array.from(yearCounts.entries())
            .sort((a, b) => b[0] - a[0]) // Most recent first
            .map(([year, count]) => ({ year, count }));

        const assets = Array.from(assetCounts.entries())
            .sort((a, b) => b[1] - a[1]) // Highest count first
            .map(([asset, count]) => ({ asset, count }));

        return { years, assets };
    };

    // Toggle year selection
    const toggleYear = (filterKey: string, year: number) => {
        setSelections(prev => {
            const current = prev[filterKey] || { years: new Set(), assets: new Set() };
            const newYears = new Set(current.years);

            if (newYears.has(year)) {
                newYears.delete(year);
            } else {
                newYears.add(year);
            }

            return {
                ...prev,
                [filterKey]: { ...current, years: newYears }
            };
        });
    };

    // Toggle asset selection
    const toggleAsset = (filterKey: string, asset: string) => {
        setSelections(prev => {
            const current = prev[filterKey] || { years: new Set(), assets: new Set() };
            const newAssets = new Set(current.assets);

            if (newAssets.has(asset)) {
                newAssets.delete(asset);
            } else {
                newAssets.add(asset);
            }

            return {
                ...prev,
                [filterKey]: { ...current, assets: newAssets }
            };
        });
    };

    // Select all years for a filter
    const selectAllYears = (filterKey: string) => {
        const { years } = getFilterData(filterKey);
        setSelections(prev => {
            const current = prev[filterKey] || { years: new Set(), assets: new Set() };
            return {
                ...prev,
                [filterKey]: {
                    ...current,
                    years: new Set(years.map(y => y.year))
                }
            };
        });
    };

    // Clear all selections for a filter
    const clearSelections = (filterKey: string) => {
        setSelections(prev => ({
            ...prev,
            [filterKey]: { years: new Set(), assets: new Set() }
        }));
    };

    // Navigate with filters
    const handleViewClick = (filterKey: string) => {
        const params = new URLSearchParams();

        if (filterKey !== 'all') {
            params.set('filter', filterKey);
        }

        const filterSelections = selections[filterKey];
        if (filterSelections) {
            // Add selected years
            if (filterSelections.years.size > 0) {
                const yearsArray = Array.from(filterSelections.years);
                yearsArray.forEach(year => params.append('year', year.toString()));
            }

            // Add selected assets
            if (filterSelections.assets.size > 0) {
                const assetsArray = Array.from(filterSelections.assets);
                assetsArray.forEach(asset => params.append('asset', asset));
            }
        }

        navigate(`/smart-check/issues?${params.toString()}`);
    };

    // Impact filter cards configuration - show card if there are active OR ignored issues
    const impactFilterCards: FilterCard[] = [
        ...(highImpactIssues.length > 0 || highIgnoredCount > 0 ? [{
            key: 'high',
            label: t('smartCheck.filters.high'),
            count: highImpactIssues.length,
            impact: formatCurrency(highImpactAmount, userCurrency),
            color: theme.palette.error.main,
            icon: <ErrorOutlineIcon />,
            description: t('smartCheck.overview.highDesc', 'Issues with tax impact > €10'),
        }] : []),
        ...(lowImpactIssues.length > 0 || lowIgnoredCount > 0 ? [{
            key: 'low',
            label: t('smartCheck.filters.low'),
            count: lowImpactIssues.length,
            impact: formatCurrency(lowImpactAmount, userCurrency),
            color: theme.palette.warning.main,
            icon: <WarningAmberIcon />,
            description: t('smartCheck.overview.lowDesc', 'Issues with tax impact ≤ €10'),
        }] : []),
        ...(missingPriceIssues.length > 0 || pricingIgnoredCount > 0 ? [{
            key: 'pricing',
            label: t('smartCheck.filters.pricing'),
            count: missingPriceIssues.length,
            impact: t('smartCheck.filters.unknown'),
            color: theme.palette.info.main,
            icon: <AssessmentIcon />,
            description: t('smartCheck.overview.pricingDesc', 'Transactions missing price data'),
        }] : []),
    ];

    if (isLoading) {
        return (
            <PageLayout>
                <Box sx={{ mb: 4 }}>
                    <Skeleton variant="text" width={300} height={48} />
                    <Skeleton variant="text" width={500} height={24} sx={{ mt: 1 }} />
                </Box>
                <Grid container spacing={2}>
                    {[1, 2, 3, 4, 5].map((i) => (
                        <Grid item xs={12} sm={6} md={6} lg={4} key={i}>
                            <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 2 }} />
                        </Grid>
                    ))}
                </Grid>
            </PageLayout>
        );
    }

    // Show "all good" message only if there are NO active issues AND NO ignored issues
    if (totalIssues === 0 && ignoredIssuesCount === 0) {
        return (
            <PageLayout>
                <Box sx={{ textAlign: 'center', py: 8 }}>
                    <CheckCircleIcon sx={{ fontSize: 64, color: theme.palette.success.main, mb: 2 }} />
                    <Typography variant="h4" sx={{ mb: 1, fontWeight: 600, color: theme.palette.success.main }}>
                        {t('smartCheck.boxes.allGood')}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        {t('smartCheck.overview.noIssues', 'No tax issues found in your transactions')}
                    </Typography>
                </Box>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            {/* Filter Cards */}
            <Stack spacing={3}>
                {impactFilterCards.map((card) => {
                        const currentSelections = selections[card.key];
                        const { years, assets } = getFilterData(card.key, currentSelections);
                        const selectedYears = currentSelections?.years || new Set();
                        const selectedAssets = currentSelections?.assets || new Set();
                        const isExpanded = expandedAssets.has(card.key);
                        const isFilterExpanded = expandedFilters.has(card.key);
                        const visibleAssets = isExpanded ? assets : assets.slice(0, 5);
                        const hasMoreAssets = assets.length > 5;
                        const hasFilters = years.length > 0 || assets.length > 0;

                        // Get ignored count for this card
                        const ignoredCount = card.key === 'high' ? highIgnoredCount
                                           : card.key === 'low' ? lowIgnoredCount
                                           : card.key === 'pricing' ? pricingIgnoredCount
                                           : 0;

                        return (
                            <Card
                                key={card.key}
                                elevation={0}
                                sx={{
                                    border: '1px solid',
                                    borderColor: theme.palette.divider,
                                    borderRadius: 1,
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        borderColor: card.color,
                                        boxShadow: `0 2px 8px ${alpha(card.color, 0.12)}`,
                                    },
                                }}
                            >
                                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                    {/* Main Card Content - 2 Column Layout */}
                                    <Box sx={{
                                        display: 'flex',
                                        flexDirection: { xs: 'column', md: 'row' },
                                        gap: 3,
                                        alignItems: { xs: 'flex-start', md: 'center' },
                                        justifyContent: 'space-between'
                                    }}>
                                        {/* Left Column: Issue Info */}
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 2,
                                            flex: 1,
                                            minWidth: 0
                                        }}>
                                            {/* Icon */}
                                            <Box sx={{
                                                color: card.color,
                                                display: 'flex',
                                                p: 1.5,
                                                borderRadius: 1.5,
                                                bgcolor: alpha(card.color, 0.1),
                                                flexShrink: 0
                                            }}>
                                                {card.icon}
                                            </Box>

                                            {/* Issue Details - 2-line layout for better readability */}
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                {/* Line 1: Label, Count, and Ignored Badge */}
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                                                    <Typography
                                                        variant="body1"
                                                        component="span"
                                                        sx={{
                                                            fontWeight: 600,
                                                            color: 'text.primary'
                                                        }}
                                                    >
                                                        {card.label}
                                                    </Typography>
                                                    <Typography variant="body1" component="span" color="text.secondary">•</Typography>
                                                    <Typography
                                                        variant="body1"
                                                        component="span"
                                                        sx={{
                                                            fontWeight: 600,
                                                            color: 'text.secondary'
                                                        }}
                                                    >
                                                        {card.count}
                                                    </Typography>
                                                    <Typography variant="body1" component="span" color="text.secondary">
                                                        {t('smartCheck.overview.issues', { count: card.count, defaultValue: card.count === 1 ? 'issue' : 'issues' })}
                                                    </Typography>
                                                    {ignoredCount > 0 && (
                                                        <>
                                                            <Typography variant="body1" component="span" color="text.secondary">•</Typography>
                                                            <Chip
                                                                icon={<VisibilityOffIcon sx={{ fontSize: 16 }} />}
                                                                label={`${ignoredCount} ${t('smartCheck.filters.ignored')}`}
                                                                size="small"
                                                                sx={{
                                                                    height: 24,
                                                                    fontSize: '0.875rem',
                                                                    bgcolor: alpha(theme.palette.action.disabled, 0.08),
                                                                    color: 'text.secondary',
                                                                    '& .MuiChip-icon': {
                                                                        color: 'text.secondary'
                                                                    }
                                                                }}
                                                            />
                                                        </>
                                                    )}
                                                </Box>
                                                {/* Line 2: Impact amount with context (only if numeric) */}
                                                {card.impact && card.impact !== t('smartCheck.filters.unknown') && (
                                                    <Box>
                                                        <Typography
                                                            variant="h6"
                                                            component="div"
                                                            sx={{ fontWeight: 700, color: card.color }}
                                                        >
                                                            {t('smartCheck.overview.upTo', 'Bis zu')} {card.impact} {t('smartCheck.overview.additionalTaxes', 'zusätzliche Steuern')}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        </Box>

                                        {/* Right Column: Actions */}
                                        <Box sx={{
                                            display: 'flex',
                                            gap: 1.5,
                                            alignItems: 'center',
                                            width: { xs: '100%', md: 'auto' }
                                        }}>
                                            <Button
                                                variant="contained"
                                                size="medium"
                                                onClick={() => handleViewClick(card.key)}
                                                sx={{
                                                    bgcolor: card.color,
                                                    color: '#fff',
                                                    minWidth: { xs: '100%', md: '140px' },
                                                    boxShadow: 'none',
                                                    '&:hover': {
                                                        bgcolor: card.color,
                                                        opacity: 0.9,
                                                        boxShadow: 'none',
                                                    },
                                                }}
                                            >
                                                {t('smartCheck.overview.viewIssues', 'View Issues')}
                                            </Button>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        );
                    })}
            </Stack>
        </PageLayout>
    );
};

export default SmartCheckOverview;
